import { Breadcrumbs } from './Breadcrumbs'
import { ActionButtons } from './ActionButtons'
import { ViewModeSwitch } from '../filters/ViewModeSwitch'
import { TimeFilter } from '../filters/TimeFilter'
import { SortByFilter } from '../filters/SortByFilter'
import { SearchBox } from '../filters/SearchBox'
import { ProjectTags } from '../projects/ProjectTags'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { useRouter } from 'next/router'
import React from 'react'
import Container from '@mui/material/Container'
import { isEmpty, isNil } from '@/helpers/helpers'
import { SortByFilterType, TimeFilterType, ViewModeType } from '@/interfaces/filters'
import { BreadcrumbItem } from '@/interfaces/pages'
import { Project } from '@/interfaces/projects'
import { SPACING_VARIABLES } from '@/theme/themeOptions/vars'
import { ZINDEXES } from '@/constants/zindexes'
import { useAuthContext } from '@/context/auth'

interface SubheaderProps {
  title: string
  breadcrumbs?: BreadcrumbItem[]
  searchString?: string
  sortByFilter?: SortByFilterType
  timeFilter?: TimeFilterType
  viewMode?: ViewModeType
  project?: Project
  showSearch?: boolean
  showSortByFilters?: boolean
  showTimeFilters?: boolean
  showViewMode?: boolean
  showActionButtons?: boolean
  onIndexPage?: boolean
}

interface SubheaderStyledProps {
  isBannerVisible?: boolean
  isOnIndexPage?: boolean
}

const SubheaderStyled = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isBannerVisible' && prop !== 'isOnIndexPage'
})<SubheaderStyledProps>(({ theme, isBannerVisible, isOnIndexPage }) => {
  return {
    marginBottom: theme.spacing(6),
    paddingTop: theme.spacing(4),
    top: !isBannerVisible
      ? SPACING_VARIABLES.APP_HEADER_HEIGHT
      : SPACING_VARIABLES.APP_HEADER_HEIGHT + SPACING_VARIABLES.BANNER_HEIGHT,
    position: 'sticky',
    zIndex: ZINDEXES.SUBHEADER_LAYER,
    backgroundColor: theme.palette.grey['50'],
    transition: 'all .25s ease-in-out',
    marginLeft: isOnIndexPage ? theme.spacing(-3) : theme.spacing(0),
    marginRight: isOnIndexPage ? theme.spacing(-3) : theme.spacing(0),
    [theme.breakpoints.down('sm')]: {
      top: !isBannerVisible
        ? SPACING_VARIABLES.MOBILE_APP_HEADER_HEIGHT
        : SPACING_VARIABLES.MOBILE_APP_HEADER_HEIGHT + SPACING_VARIABLES.BANNER_HEIGHT,
      position: 'static'
    }
  }
})

const Subheader = ({
  title,
  breadcrumbs,
  searchString,
  sortByFilter,
  timeFilter,
  viewMode,
  project,
  showSearch,
  showSortByFilters,
  showTimeFilters,
  showViewMode,
  showActionButtons,
  onIndexPage
}: SubheaderProps): React.ReactElement => {
  const { isBannerDisplayed } = useAuthContext()
  const router = useRouter()
  const onTagsPage = router.pathname === '/tags'

  return (
    <SubheaderStyled qa-attribute="subheader" isBannerVisible={isBannerDisplayed} isOnIndexPage={onIndexPage}>
      <Container>
        <Grid container spacing={6}>
          <Grid item xs={12} md={12} lg={12}>
            <Stack direction="column" gap={2} sx={{ borderBottom: 1, borderColor: 'divider', paddingBottom: 6 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-start">
                <Stack direction="column" gap={2} width={{ xs: '100%', md: 'auto' }}>
                  {!isEmpty(breadcrumbs) && (
                    <Box sx={{ marginRight: 'auto' }}>
                      <Breadcrumbs breadcrumbs={breadcrumbs} qa-attribute="projectBreadcrumbs" />
                    </Box>
                  )}
                  <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                    <Typography sx={{ typography: { xs: 'h5', sm: 'h1' }, wordBreak: 'break-all' }} component="h1">
                      {title}
                    </Typography>
                    {!isNil(project) && (
                      <ProjectTags
                        isPrivate={!project.publicPermission?.readPreview}
                        isShared={project.isSharedWithRequester}
                        inline
                      />
                    )}
                  </Stack>
                </Stack>
                <Stack
                  sx={{ marginLeft: { sm: 'auto' }, marginTop: 3 }}
                  width={{ xs: '100%', md: 'auto' }}
                  alignItems="center"
                >
                  {showActionButtons && <ActionButtons project={project} />}
                  {showSearch && onTagsPage && <SearchBox searchString={searchString} />}
                </Stack>
              </Stack>

              {((showSearch && !onTagsPage) || showSortByFilters || showTimeFilters || showViewMode) && (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  gap={{ xs: 5, sm: 2 }}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  {(showSearch || showSortByFilters) && (
                    <Stack
                      width={{ xs: '100%', md: 'auto' }}
                      direction="row"
                      gap={{ xs: 5, sm: 2 }}
                      justifyContent={{ md: 'left', xs: 'center' }}
                      alignItems="center"
                    >
                      {showSearch && !onTagsPage && <SearchBox searchString={searchString} />}
                      {showTimeFilters && <TimeFilter timeFilter={timeFilter} />}
                    </Stack>
                  )}
                  {(showTimeFilters || showViewMode) && (
                    <Stack
                      width={{ xs: '100%' }}
                      direction="row"
                      gap={{ xs: 5, sm: 2 }}
                      justifyContent={{ md: 'right', xs: 'center' }}
                      alignItems="center"
                    >
                      {showSortByFilters && <SortByFilter sortByFilter={sortByFilter} />}
                      {showViewMode && <ViewModeSwitch viewMode={viewMode} />}
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </SubheaderStyled>
  )
}

export default Subheader
